import { useLocation, Link } from "wouter";
import { Fuel, MapPin, BookOpen, Shield, TrendingUp, Settings, type LucideIcon } from "lucide-react";
import { NAV_ITEMS } from "./BottomNav";

// Core tools — shown in both mobile bottom nav and desktop sidebar
const CORE_NAV = NAV_ITEMS.filter((i) => i.path !== "/settings" && i.path !== "/fuel-security");

// Discover section — desktop sidebar only
const DISCOVER_NAV = [
  { path: "/fuel-security", label: "Fuel Security", icon: Shield },
  { path: "/trends", label: "Trends", icon: TrendingUp },
  { path: "/prices", label: "Suburbs", icon: MapPin },
  { path: "/blog", label: "Guides", icon: BookOpen },
];

function NavButton({ path, label, icon: Icon, isActive }: { path: string; label: string; icon: LucideIcon; isActive: boolean }) {
  return (
    <Link href={path}>
      <button
        type="button"
        className={`
          flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
          ${isActive
            ? "bg-primary/15 text-primary border border-primary/20"
            : "text-muted-foreground hover:text-foreground hover:bg-white/5"
          }
        `}
      >
        <Icon size={18} />
        <span>{label}</span>
      </button>
    </Link>
  );
}

export function Sidebar() {
  const [location] = useLocation();

  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 z-50 glass-panel border-r border-white/10">
      {/* Logo */}
      <div className="flex items-center gap-2 px-6 h-16 border-b border-white/5">
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary border border-primary/30">
          <Fuel size={18} strokeWidth={2.5} />
        </div>
        <span className="font-bold text-lg tracking-tight">
          Aussie<span className="text-primary">Petrol</span>
        </span>
      </div>

      {/* Core navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {CORE_NAV.map((item) => (
          <NavButton key={item.path} {...item} isActive={location === item.path} />
        ))}

        {/* Discover section */}
        <div className="pt-4 mt-4 border-t border-white/5">
          <span className="px-4 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Discover
          </span>
          <div className="mt-2 space-y-1">
            {DISCOVER_NAV.map((item) => (
              <NavButton key={item.path} {...item} isActive={location === item.path || location.startsWith(item.path + "/")} />
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="pt-4 mt-4 border-t border-white/5">
          <NavButton path="/settings" label="Settings" icon={Settings} isActive={location === "/settings"} />
        </div>
      </nav>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/5">
        <p className="text-[10px] text-muted-foreground">
          Data from NSW FuelCheck API
        </p>
      </div>
    </aside>
  );
}
